import { useEffect, useMemo } from 'react';
import { InsuranceResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/api-service/v1/insurance_verification_api.pb';
import {
  InsuranceStatus,
  PayerDetail,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { prepareInsuranceDetailsForDental } from '../utils/prepare-data-utils/prepare-insurance-data';
import type { UndefinedOrNull, UseGetInsuranceDetailsDentalOrNonDentalResponse } from '../utils/types';

export const useGetDentalInsuranceDetails = (
  isDental: boolean,
  personId: string,
  locationId: string,
  insurancePersonDetails: UndefinedOrNull<InsuranceResponse>,
  payerListResponse: UndefinedOrNull<PayerDetail[]>,
  forceFetch: boolean
): UseGetInsuranceDetailsDentalOrNonDentalResponse | null => {
  //This Api is responsible for getting only dental coverage details of particular person
  const hasCoverageDetails = !!insurancePersonDetails?.coverage?.thirdPartyCoverage?.coverage;

  const {
    data: insuranceVerficationDetails,
    isLoading,
    refetch,
  } = InsuranceDetailQueries.useGetDentalInsuranceVerificationDetailsPersonId({
    personId,
    locationId: locationId || '',
    options: {
      enabled: isDental && hasCoverageDetails && !!personId && !!locationId,
    },
  });

  //This refetches InsuranceVerificationDetailsPersonId only when forceFetch is true
  useEffect(() => {
    if (forceFetch) {
      refetch();
    }
  }, [forceFetch]);

  //This preparesInsuranceDetailsForDental including payerListResponse by sanatizing the data and removing unwanted chars
  const insuranceDetails = useMemo(() => {
    return prepareInsuranceDetailsForDental(
      insurancePersonDetails,
      payerListResponse,
      isDental,
      insuranceVerficationDetails?.coverage
    );
  }, [insuranceVerficationDetails, isDental, payerListResponse, insuranceVerficationDetails?.coverage]);

  const hasActiveOrVerifiedInsurance =
    insuranceDetails?.thirdPartyCoverageStatus.toUpperCase() === InsuranceStatus.ACTIVE ||
    insuranceDetails?.thirdPartyCoverageStatus.toUpperCase() === InsuranceStatus.VERIFIED;
  const thirdPartyCoverage = insuranceVerficationDetails?.coverage?.thirdPartyCoverage;

  return {
    isLoading,
    insuranceDetails,
    thirdPartyCoverage,
    hasActiveOrVerifiedInsurance,
  };
};
